import React, { useState, useEffect, useRef } from 'react'
import { SendNoiseReport as BLSendNoiseReport } from './../../../utilities/BL/Admin/Overlays/NoisemeterV2'
import { GetActiveStadiumZones as BLGetActiveStadiumZones } from './../../../utilities/BL/Zones'


const NoisemeterV2 = ({ config, winners, userZone }) => {
    const activeUserZone = userZone || localStorage.getItem('user_zone');
    
    // --- ESTADOS ---
    const [gameState, setGameState] = useState('LOADING'); 
    const [volumeDisplay, setVolumeDisplay] = useState(0); 
    const [countdownVal, setCountdownVal] = useState(3);
    const [timeLeft, setTimeLeft] = useState(0); 
    const [winnerMessage, setWinnerMessage] = useState(null);
    const [zoneLabels, setZoneLabels] = useState({});

    // --- REFS ---
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const volumeAccumulator = useRef(0);
    const volumeReadings = useRef(0);
    
    const reportingInterval = useRef(null);
    const animationRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const gameTimerRef = useRef(null);
    const wakeLockReference = useRef(null);
    const streamRef = useRef(null); // Ref para el stream del micrófono para apagarlo al desmontar
    const isMountedRef = useRef(true); // Ref para rastrear si el componente sigue montado

    // -------------------------------------------------------------------------
    // MONTAJE / DESMONTAJE
    // -------------------------------------------------------------------------
    useEffect(() => {
        isMountedRef.current = true;
        initMicrophone(true); 

        const fetchZoneLabels = async () => {
            try {
                const res = await BLGetActiveStadiumZones();
                if (res.success && Array.isArray(res.data)) {
                    const mapping = {};
                    res.data.forEach(z => {
                        if (z.internalName && z.displayName) {
                            mapping[z.internalName.toLowerCase()] = z.displayName;
                        }
                    });
                    setZoneLabels(mapping);
                }
            } catch (err) {
                console.warn("[NoisemeterV2 Fan] Error cargando etiquetas de zonas:", err);
            }
        };
        fetchZoneLabels();

        return () => {
            isMountedRef.current = false;
            stopAllProcesses();
            stopMicrophone(); // Apagar micrófono al desmontar
            releaseWakeLock();
        };
    }, []);

    // -------------------------------------------------------------------------
    // WAKELOCK (BLOQUEO DE PANTALLA)
    // -------------------------------------------------------------------------
    const requestWakeLock = async () => {
        try {
            if ('wakeLock' in navigator) {
                if (wakeLockReference.current || window.activeWakeLock) {
                    // Si ya está activo, guardamos referencia local
                    if (!wakeLockReference.current) wakeLockReference.current = window.activeWakeLock;
                    return;
                }
                
                const lock = await navigator.wakeLock.request('screen');
                wakeLockReference.current = lock;
                window.activeWakeLock = lock;
                console.log("[WakeLock] 🔓 Bloqueo de pantalla activado en juego.");

                lock.addEventListener('release', () => {
                    if (window.activeWakeLock === lock) {
                        window.activeWakeLock = null;
                    }
                    if (wakeLockReference.current === lock) {
                        wakeLockReference.current = null;
                    }
                    console.log("[WakeLock] 🔒 Bloqueo de pantalla liberado.");
                });
            }
        } catch (err) {
            console.warn("[WakeLock] Error al solicitar WakeLock:", err);
        }
    };

    const releaseWakeLock = () => {
        if (window.activeWakeLock) {
            try {
                window.activeWakeLock.release();
            } catch (e) {}
            window.activeWakeLock = null;
        }
        if (wakeLockReference.current) {
            wakeLockReference.current = null;
        }
    };

    // Re-solicitar WakeLock al volver a enfocar la app
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && (gameState === 'WAITING_SIGNAL' || gameState === 'COUNTDOWN' || gameState === 'RUNNING')) {
                requestWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [gameState]);

    // -------------------------------------------------------------------------
    // LÓGICA DE CONFIGURACIÓN Y TIEMPOS
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!config) return;

        setWinnerMessage(null);

        // Validar Zona
        if (config.targetZone && config.targetZone !== 'all' && config.targetZone.toLowerCase() !== activeUserZone?.toLowerCase()) {
            stopAllProcesses();
            releaseWakeLock();
            setGameState('LOCKED');
            return;
        }

        if (!audioContextRef.current) {
            if (gameState !== 'LOADING') setGameState('PERMISSION_NEEDED');
            return;
        }

        // Si la zona es válida y el audio está listo, solicitamos bloqueo
        requestWakeLock();
        handleGameTiming();

    }, [config, activeUserZone]);

    
    const handleGameTiming = () => {
        const now = Date.now();
        const startAt = new Date(config.startAt).getTime();
        const endsAt = new Date(config.endsAt).getTime();
        const msUntilStart = startAt - now;

        stopAllProcesses(); // Limpieza preventiva

        if (msUntilStart > 0) {
            const rawSeconds = Math.ceil(msUntilStart / 1000);
            const displaySeconds = rawSeconds > 3 ? 3 : rawSeconds;
            
            startCountdown(displaySeconds, msUntilStart);
            
            setTimeout(() => {
                if (audioContextRef.current) startGameFlow(endsAt);
            }, msUntilStart);
        } else {
            startGameFlow(endsAt);
        }
    };


    // -------------------------------------------------------------------------
    // REACCIÓN A GANADORES (Backend push)
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!winners) return;
        
        stopAllProcesses(); 
        releaseWakeLock(); // Liberamos bloqueo ya que terminó el show
        
        setGameState('FINISHED');
        
        const wZone = winners.winnerZone || winners.WinnerZone;
        
        if (wZone === 'tie') setWinnerMessage('¡Ha habido un empate!');
        else if (wZone?.toLowerCase() === activeUserZone?.toLowerCase()) setWinnerMessage('¡Ganaste! 🏆');
        else {
            const friendlyName = zoneLabels[wZone?.toLowerCase()] || wZone;
            setWinnerMessage(`Ganó ${friendlyName?.toUpperCase()}`);
        }

    }, [winners, activeUserZone]);


    // -------------------------------------------------------------------------
    // AUDIO ENGINE
    // -------------------------------------------------------------------------
    const initMicrophone = async (isAutoInit = false) => {
        try {
            if (streamRef.current) {
                try {
                    streamRef.current.getTracks().forEach(track => track.stop());
                } catch (e) {}
                streamRef.current = null;
            }

            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContext();
            audioContextRef.current = ctx;
            
            const stream = await navigator.mediaDevices.getUserMedia({ audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: false
            } });

            if (!isMountedRef.current) {
                stream.getTracks().forEach(track => track.stop());
                ctx.close();
                return;
            }

            streamRef.current = stream; // Guardar stream para liberarlo al salir
            const source = ctx.createMediaStreamSource(stream);
            
            analyserRef.current = ctx.createAnalyser();
            analyserRef.current.fftSize = 256; 
            analyserRef.current.smoothingTimeConstant = 0.5;

            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.value = 1000; 

            source.connect(filter);
            filter.connect(analyserRef.current);

            if (ctx.state === 'suspended') {
                await ctx.resume();
            }

            // Al activarse el micrófono, solicitamos WakeLock de una vez
            await requestWakeLock();

            if (config) {
                handleGameTiming();
            } else {
                setGameState('WAITING_SIGNAL');
            }

        } catch (err) {
            console.warn("No se pudo iniciar micrófono automáticamente:", err);
            setGameState('PERMISSION_NEEDED');
            if (!isAutoInit) {
                alert("Es necesario activar el micrófono para jugar.");
            }
        }
    };

    const stopMicrophone = () => {
        if (streamRef.current) {
            try {
                streamRef.current.getTracks().forEach(track => {
                    track.stop();
                });
            } catch (e) {
                console.warn("[Mic V2] Error al apagar pistas:", e);
            }
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            try {
                if (audioContextRef.current.state !== 'closed') {
                    audioContextRef.current.close();
                }
            } catch (e) {
                console.warn("[Mic V2] Error al cerrar contexto:", e);
            }
            audioContextRef.current = null;
        }

        analyserRef.current = null;
    };

    // -------------------------------------------------------------------------
    // TIMERS Y LOOPS
    // -------------------------------------------------------------------------
    const startCountdown = (startSeconds, msTotal) => {
        setGameState('COUNTDOWN');
        setCountdownVal(startSeconds);
        
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        
        let current = startSeconds;
        countdownIntervalRef.current = setInterval(() => {
            current--;
            if (current > 0) setCountdownVal(current);
            else clearInterval(countdownIntervalRef.current);
        }, 1000);
    };

    const startGameTimer = (endsAtTimestamp) => {
        if (gameTimerRef.current) clearInterval(gameTimerRef.current);
        
        const updateTimer = () => {
            const now = Date.now();
            const diff = Math.ceil((endsAtTimestamp - now) / 1000);
            
            setTimeLeft(diff > 0 ? diff : 0);

            if (diff <= 0) {
                clearInterval(gameTimerRef.current);
                
                stopAllProcesses(); 
                releaseWakeLock(); // Liberar cuando el tiempo local termine

                setGameState('FINISHED');
                setWinnerMessage((prev) => prev || "Calculando ganador..."); 
            }
        };
        
        updateTimer();
        gameTimerRef.current = setInterval(updateTimer, 1000);
    };

    const startGameFlow = (endsAtTimestamp) => {
        if (!analyserRef.current) return;

        if (Date.now() >= endsAtTimestamp) {
            setGameState('FINISHED');
            setWinnerMessage("Tiempo finalizado");
            releaseWakeLock();
            return;
        }

        setGameState('RUNNING');
        requestWakeLock(); // Reforzar por seguridad
        startGameTimer(endsAtTimestamp);
        processAudioLoop();
        
        if (reportingInterval.current) clearInterval(reportingInterval.current);
        reportingInterval.current = setInterval(sendReport, 500);
    };

    const processAudioLoop = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        let average = sum / dataArray.length;
        
        let visualVal = Math.max(0, (average - 15)) * 2.0; 
        if (visualVal > 100) visualVal = 100;
        
        setVolumeDisplay(visualVal);
        volumeAccumulator.current += visualVal; 
        volumeReadings.current += 1;
        
        animationRef.current = requestAnimationFrame(processAudioLoop);
    };

    const sendReport = () => {
        if (volumeReadings.current === 0 || !config?.matchId) return;
        
        const avgVolume = volumeAccumulator.current / volumeReadings.current;
        
        if (typeof BLSendNoiseReport === 'function') {
             BLSendNoiseReport(config.matchId, activeUserZone, avgVolume);
        }
       
        volumeAccumulator.current = 0;
        volumeReadings.current = 0;
    };

    const stopAllProcesses = () => {
        if (reportingInterval.current) {
            clearInterval(reportingInterval.current);
            reportingInterval.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (gameTimerRef.current) {
            clearInterval(gameTimerRef.current);
            gameTimerRef.current = null;
        }
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
    };

    return (
        <div className="noisemeter">
            <div className="noisemeter__card">
                
                {gameState === 'LOADING' && (
                    <div className="noisemeter__step">
                        <div className="spinner-border text-primary " role="status"></div>
                        <p className='Noisemeter__Fan__Text--Center'>Verificando micrófono...</p>
                    </div>
                )}

                {gameState === 'PERMISSION_NEEDED' && (
                    <div className="noisemeter__step">
                        <p className='Noisemeter__Fan__Text--Danger Noisemeter__Fan__Text--Big Noisemeter__Fan__Text--Center Noisemeter__Fan__Element--ZeroBottom'>
                            <i className="bi bi-mic-mute-fill"></i>
                        </p>
                        <p className="Noisemeter__Fan__Text--Center">Permiso denegado</p>
                        <p className="Noisemeter__Fan__Text--Center">Entraste tarde o no diste permiso antes.</p>
                        <p className="Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Highlight">Actívalo ahora para unirte al juego</p>
                        <button className="FrontPageNoisemeter__Fan__Element--Center FrontPageNoisemeter__Fan__Button Btn__Adsl--Red" onClick={() => initMicrophone(false)}>
                            <i className="bi bi-mic-fill"></i> Activar micrófono ahora
                        </button>
                    </div>
                )}

                {gameState === 'WAITING_SIGNAL' && (
                    <div className="noisemeter__step noisemeter__step--waiting">
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Big'><i className="bi bi-broadcast-pin"></i></p>
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Success'>¡Estás listo para el Ladrómetro!</p>
                        <p className='Noisemeter__Fan__Text--Center'>Esperando señal de inicio...</p>
                    </div>
                )}

                {gameState === 'LOCKED' && (
                    <div className="noisemeter__step">
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Big Noisemeter__Fan__Element--ZeroBottom'>
                            <i className="bi bi-lock-fill" style={{ color: '#999' }}></i>
                        </p>
                        <p className='Noisemeter__Fan__Text--Center'>Esperando tu turno.</p>
                        <p className='Noisemeter__Fan__Text--Center'>Por ahora está jugando otra zona</p>
                    </div>
                )}

                {gameState === 'COUNTDOWN' && (
                    <div className="noisemeter__step">
                        <p className="Noisemeter__Fan__Text--Danger Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Medium">Grita en...</p>
                        <span className="noisemeter__countdown-number Noisemeter__Fan__Text--Danger">{countdownVal}</span>
                    </div>
                )}

                {gameState === 'RUNNING' && (
                    <div className="noisemeter__step">
                        <p className="Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--ZeroBottom Noisemeter__Fan__Text--Medium">¡Grita ahora!</p>
                        <div className="noisemeter__timer" style={{fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '15px', color: '#555'}}>
                            <i className="bi bi-stopwatch-fill"></i> {timeLeft}s
                        </div>
                        <div className="noisemeter__meter-container Noisemeter__Fan__Element--ZeroBottom">
                            <div className={`noisemeter__meter-fill`} style={{ width: `${volumeDisplay}%`, background: `linear-gradient(90deg, #4caf50, ${volumeDisplay > 70 ? '#f44336' : '#ffeb3b'})`}}></div>
                        </div>
                        <p className='Noisemeter__Fan__Text--Center Noisemeter__Fan__Text--Small'>Intensidad de tu grito</p>
                    </div>
                )}

                {gameState === 'FINISHED' && (
                    <div className="noisemeter__step">
                        <p className=''>¡Tiempo finalizado!</p>
                        <div className=''>
                            <p className='Noisemeter__Fan__Text--Highlight Noisemeter__Fan__Text--Medium'>
                                {winnerMessage || "Calculando..."}
                            </p>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export { NoisemeterV2 };
